import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/apiService';
import BackgroundShapes from '../common/BackgroundShapes';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim());
      // Always show success state — never reveal whether email exists (prevents enumeration)
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-coffee mb-2">Forgot Password</h1>
            <p className="text-coffee opacity-80">We'll send you a reset link</p>
          </div>

          {/* Card */}
          <div className="bg-cream border-2 border-coffee rounded-xl p-8 shadow-md">
            {submitted ? (
              /* Success state */
              <div className="text-center space-y-4">
                <div className="text-5xl mb-2">📬</div>
                <h2 className="text-xl font-bold text-coffee">Check your email</h2>
                <p className="text-coffee opacity-80 text-sm">
                  If an account with that email exists, we've sent a password reset link. It expires in 1 hour.
                </p>
                <p className="text-coffee opacity-60 text-xs">
                  Don't see it? Check your spam folder.
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-terracotta text-white py-3 rounded-lg font-bold text-lg hover:bg-[#d89050] transition-all duration-300 border-2 border-coffee shadow-lg mt-4"
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              /* Email input form */
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-coffee text-sm font-medium mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 rounded-lg bg-white border border-coffee text-coffee placeholder-coffee placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta transition-colors"
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-terracotta text-white py-3 rounded-lg font-bold text-lg hover:bg-[#d89050] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center border-2 border-coffee shadow-lg"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Back link */}
          {!submitted && (
            <div className="text-center mt-6">
              <button
                onClick={() => navigate('/login')}
                className="text-coffee hover:text-terracotta text-sm transition-colors"
              >
                ← Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
